(() => {
  const dialog = document.getElementById('BmhShippingDialog');
  if (!dialog) return;
  const messages = JSON.parse(dialog.querySelector('[data-bmh-shipping-translations]').textContent);
  const product = JSON.parse(dialog.querySelector('[data-bmh-shipping-product]').textContent);
  const noticeTemplate = dialog.querySelector('[data-bmh-shipping-notice-template]');
  const form = dialog.querySelector('form');
  const province = form.elements.province;
  const zip = form.elements.zip;
  const zipField = dialog.querySelector('[data-bmh-shipping-zip-field]');
  const status = dialog.querySelector('[data-bmh-shipping-status]');
  const results = dialog.querySelector('[data-bmh-shipping-results]');
  const footnote = dialog.querySelector('[data-bmh-shipping-footnote]');
  const submit = form.querySelector('[type="submit"]');
  const forget = dialog.querySelector('[data-bmh-shipping-forget]');
  const destinationKey = 'bmh-shipping-destination-v2';
  const destinationLifetime = 30 * 24 * 60 * 60 * 1000;
  const root = window.Shopify?.routes?.root || '/';
  const money = new Intl.NumberFormat(document.documentElement.lang?.startsWith('es') ? 'es-AR' : 'en-US', {
    style: 'currency', currency: 'ARS', currencyDisplay: 'narrowSymbol', maximumFractionDigits: 0
  });
  let rules;
  let rulesPromise;
  let controller;
  let cartCache;
  let destinationMemory;
  let ratesCache;
  let cartCacheAt = 0;
  let opener;
  let openerControl;
  let openSequence = 0;
  let quoteSequence = 0;
  let autoCloseTimer;
  let closingTimer;
  const done = dialog.querySelector('[data-bmh-shipping-done]');
  let forcedZip = false;
  let timer;
  let deliveryTimer;
  let noticeTimer;
  let noticeRequest = 0;
  let noticeController;
  let noticeState;
  let noticeRefreshTimer;
  const text = (key, values = {}) => String(messages[key] || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name) => values[name] ?? '');
  const amount = cents => money.format(cents / 100);
  function validDestination(saved, now = Date.now()) {
    if (!saved || typeof saved.province !== 'string' || typeof saved.zip !== 'string') return null;
    if (!Number.isFinite(saved.savedAt) || now - saved.savedAt >= destinationLifetime || saved.savedAt > now + 60000) return null;
    if (rules && !rules.zones.some(zone => zone.provinces.some(item => item.code === saved.province))) return null;
    if (!/^[A-Z]$/.test(saved.province)) return null;
    const postal = saved.zip.trim().toUpperCase();
    if (postal && !/^([0-9]{4}|[A-Z][0-9]{4}[A-Z]{3})$/.test(postal)) return null;
    if (saved.province === 'B' && !postal) return null;
    if (/^[A-Z]/.test(postal) && postal[0] !== saved.province) return null;
    return {province: saved.province, zip: postal, savedAt: saved.savedAt};
  }
  function readDestination() {
    let raw;
    try { raw = localStorage.getItem(destinationKey); } catch {}
    if (!raw) try { raw = sessionStorage.getItem(destinationKey); } catch {}
    if (!raw && destinationMemory) return validDestination(destinationMemory);
    if (raw) {
      try { return validDestination(JSON.parse(raw)); } catch { return null; }
    }
    try {
      const legacy = JSON.parse(sessionStorage.getItem('bmh-shipping-destination') || 'null');
      const migrated = validDestination(legacy && {...legacy, savedAt: Date.now()});
      if (migrated) writeDestination(migrated);
      return migrated;
    } catch { return null; }
  }
  function writeDestination(saved) {
    destinationMemory = saved;
    const value = JSON.stringify(saved);
    try { localStorage.setItem(destinationKey, value); } catch {}
    try { sessionStorage.setItem(destinationKey, value); sessionStorage.removeItem('bmh-shipping-destination'); } catch {}
    if (forget) forget.hidden = false;
  }
  function clearNotices() {
    noticeState = null;
    clearInterval(noticeTimer);
    noticeTimer = null;
    document.querySelectorAll('[data-bmh-shipping-notice]').forEach(slot => {
      slot.hidden = true;
      slot.replaceChildren();
      delete slot.dataset.destination;
      delete slot.dataset.postalKey;
    });
  }
  const zoneFor = () => rules.zones.find(zone => zone.provinces.some(item => item.code === province.value));
  const check = (condition, total, weight) => {
    const value = condition.field === 'TOTAL_WEIGHT' ? weight : total;
    return condition.operator === 'GREATER_THAN_OR_EQUAL_TO' ? value >= condition.value : value <= condition.value;
  };
  const freeMinimum = (zone, name, weight) => {
    const matches = zone.methods.filter(method => method.price === 0 && method.name === name &&
      method.conditions.filter(condition => condition.field === 'TOTAL_WEIGHT').every(condition => check(condition, 0, weight)));
    const amounts = matches.flatMap(method => method.conditions.filter(condition => condition.field === 'TOTAL_PRICE' &&
      condition.operator === 'GREATER_THAN_OR_EQUAL_TO').map(condition => condition.value));
    return amounts.length ? Math.min(...amounts) : null;
  };
  const showZip = required => {
    zipField.hidden = !required;
    zip.required = required;
    if (!required) zip.value = '';
  };
  const reset = () => {
    ++quoteSequence;
    clearTimeout(autoCloseTimer);

    clearTimeout(timer);
    controller?.abort();
    controller = null;
    clearInterval(deliveryTimer);
    deliveryTimer = null;
    submit.disabled = false;
    submit.textContent = text('calculate');
    results.replaceChildren();
    if (done) done.hidden = true;
    footnote.hidden = true;
    status.textContent = '';
    dialog.dataset.state = 'idle';
    submit.removeAttribute('aria-busy');
  };
  async function loadRules() {
    if (rules) return;
    if (!rulesPromise) {
      const loading = new AbortController();
      const deadline = setTimeout(() => loading.abort(), 8000);
      rulesPromise = fetch(dialog.dataset.rulesUrl, {signal: loading.signal}).then(response => {
        if (!response.ok) throw new Error('rules');
        return response.json();
      }).catch(error => { rulesPromise = null; throw error; }).finally(() => clearTimeout(deadline));
    }
    const loadedRules = await rulesPromise;
    if (rules) return;
    rules = loadedRules;
    const provinces = rules.zones.flatMap(zone => zone.provinces)
      .sort((a, b) => a.name.localeCompare(b.name, 'es-AR'));
    for (const item of provinces) province.add(new Option(item.name, item.code));
    {
      const saved = readDestination();
      if (saved && provinces.some(item => item.code === saved.province)) {
        province.value = saved.province;
        showZip(true);
        zip.value = saved.zip || '';
        forcedZip = Boolean(saved.zip) && saved.province !== 'B';
        if (forget) forget.hidden = false;
      }
    }
  }
  async function request(url, options, signal) {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal });
    const raw = await response.text();
    let data = null;
    if (raw) {
      try { data = JSON.parse(raw); } catch { throw new Error('response'); }
    }
    if (!response.ok) {
      const error = new Error('request');
      error.zipRequired = response.status === 422 && /zip|postal|c[oó]digo/i.test(JSON.stringify(data));
      throw error;
    }
    return data;
  }
  async function readCart(signal) {
    if (cartCache && Date.now() - cartCacheAt < 10000) return cartCache;
    const cart = await request(`${root}cart.js`, {}, signal);
    if (!Array.isArray(cart?.items) || !Number.isFinite(cart.item_count)) throw new Error('cart');
    if (!signal?.aborted) { cartCache = cart; cartCacheAt = Date.now(); }
    return cart;
  }
  async function liveRates(destination, signal, cart) {
    const key = JSON.stringify([destination, cart.total_price, cart.total_weight, cart.items.map(item => [item.key, item.quantity])]);
    if (ratesCache?.key === key && Date.now() - ratesCache.at < 30000) return ratesCache.rates;
    const parameters = new URLSearchParams({
      'shipping_address[country]': 'Argentina',
      'shipping_address[province]': destination.name,
      'shipping_address[zip]': destination.zip
    });
    await request(`${root}cart/prepare_shipping_rates.json?${parameters}`, {method: 'POST'}, signal);
    for (let attempt = 0; attempt < 12; attempt++) {
      const data = await request(`${root}cart/async_shipping_rates.json?${parameters}`, {}, signal);
      if (data && Array.isArray(data.shipping_rates)) {
        ratesCache = {key, rates: data.shipping_rates, at: Date.now()};
        return data.shipping_rates;
      }
      await new Promise((resolve, reject) => {
        const onAbort = () => { clearTimeout(timeout); reject(new DOMException('Aborted', 'AbortError')); };
        const timeout = setTimeout(() => { signal.removeEventListener('abort', onAbort); resolve(); }, 700 + attempt * 100);
        signal.addEventListener('abort', onAbort, {once: true});
        if (signal.aborted) onAbort();
      });
    }
    throw new Error('timeout');
  }
  function productContext() {
    if (!product || product.isCombo) return null;
    const input = document.querySelector('[id^="ProductInformation"] input[name="id"],product-form-component input[name="id"]');
    const variant = product.variants.find(item => String(item.id) === input?.value) ||
      product.variants.find(item => item.id === product.selected);
    const quantityInput = document.querySelector('[id^="ProductInformation"] input[name="quantity"]');
    const quantity = Math.max(1, Number(quantityInput?.value) || 1);
    return variant ? {total: variant.price * quantity, weight: variant.weight * quantity, title: product.title, quantity} : null;
  }
  function configuredRates(zone, context) {
    const eligible = context ? zone.methods.filter(method => method.conditions.every(condition => check(condition, context.total, context.weight))) :
      zone.methods.filter(method => method.conditions.every(condition => condition.field !== 'TOTAL_PRICE'));
    const rates = new Map();
    for (const method of eligible) {
      const existing = rates.get(method.name);
      if (!existing || method.price < existing.price) rates.set(method.name, method);
    }
    return [...rates.values()].map(method => ({name: method.name, price: method.price / 100,
      description: method.description, conditions: method.conditions}));
  }
  function includeLocalDelivery(rates, destination = {province: province.value, zip: zip.value}) {
    const local = rules.localDelivery;
    if (!local?.enabled) return rates;
    const postal = destination.zip.trim().toUpperCase();
    const numericPostal = postal.match(/^([A-Z]?)([0-9]{4})([A-Z]{3})?$/)?.[2];
    const nativeLocal = rates.find(rate => /^(local delivery|envío local(?: a domicilio)?)$/i.test(rate.name));
    const eligible = (!postal && local.fullProvinceCodes.includes(destination.province)) ||
      local.postalCodesByProvince[destination.province]?.includes(numericPostal);
    if (!eligible && !nativeLocal) return rates;
    const otherRates = rates.filter(rate => rate !== nativeLocal);
    return [{name: text('local_title'), price: nativeLocal?.price ?? local.price / 100, description: text('local_detail'), isLocal: true}, ...otherRates];
  }
  function preferredRates(rates) {
    const unique = new Map();
    for (const rate of rates) {
      if (!Number.isFinite(Number(rate.price)) || Number(rate.price) < 0) continue;
      const normalized = {...rate, isFlex: /\bflex\b/i.test(rate.name)};
      const previous = unique.get(rate.name);
      if (!previous || Number(rate.price) < Number(previous.price)) unique.set(rate.name, normalized);
    }
    return [...unique.values()].sort((a, b) => Number(b.isFlex || b.isLocal || false) - Number(a.isFlex || a.isLocal || false) || Number(a.price) - Number(b.price));
  }
  function deliveryWindow(now, settings) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: settings.timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
    }).formatToParts(now);
    const values = Object.fromEntries(parts.map(part => [part.type, part.value]));
    const local = new Date(Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day),
      Number(values.hour), Number(values.minute), Number(values.second), now.getUTCMilliseconds()));
    const cutoff = new Date(local);
    cutoff.setUTCHours(settings.cutoffHour, 0, 0, 0);
    const businessDay = settings.businessDays.includes(local.getUTCDay());
    const today = businessDay && local < cutoff;
    const next = new Date(local);
    next.setUTCHours(12, 0, 0, 0);
    do { next.setUTCDate(next.getUTCDate() + 1); } while (!settings.businessDays.includes(next.getUTCDay()));
    const localNoon = Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate(), 12);
    return {today, businessDay, remaining: today ? Math.ceil((cutoff - local) / 1000) : 0, next,
      arrivesTomorrow: next.getTime() - localNoon === 86400000, deliveryDate: today ? local : next};
  }
  function deliveryCopy(current) {
    const date = current.deliveryDate;
    const formattedDate = `${String(date.getUTCDate()).padStart(2, '0')}/${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
    const locale = document.documentElement.lang?.startsWith('es') ? 'es-AR' : 'en-US';
    const label = current.today ? text('arrives_today') : current.arrivesTomorrow ? text('arrives_tomorrow') :
      text('arrives_next', {day: new Intl.DateTimeFormat(locale, {timeZone: 'UTC', weekday: 'long'}).format(date)});
    return {label, date: formattedDate};
  }
  function renderDeliveryHeadline(headline, current) {
    const copy = deliveryCopy(current);
    const emphasis = document.createElement('strong');
    emphasis.textContent = copy.label;
    const content = headline.querySelector('[data-bmh-notice-headline-copy]') || headline;
    content.replaceChildren(emphasis, `, ${copy.date}`);
  }
  function tickNotices() {
    if (!noticeState || !rules?.deliverySchedule) return;
    if (!validDestination(noticeState.destination)) { clearNotices(); return; }
    const current = deliveryWindow(new Date(), rules.deliverySchedule);
    const day = current.deliveryDate.toISOString().slice(0, 10);
    const units = [Math.floor(current.remaining / 3600), Math.floor(current.remaining / 60) % 60, current.remaining % 60];
    const clock = units.map(value => String(value).padStart(2, '0')).join(':');
    document.querySelectorAll('[data-bmh-shipping-notice][data-local="true"]:not([hidden])').forEach(slot => {
      const headline = slot.querySelector('[data-bmh-notice-headline]');
      const copy = slot.querySelector('[data-bmh-notice-copy]');
      const digits = slot.querySelector('[data-bmh-notice-clock]');
      if (!headline || !copy || !digits) return;
      const phase = `${current.today}:${current.remaining <= 3600}:${day}`;
      if (slot.dataset.phase !== phase) {
        slot.dataset.phase = phase;
        slot.dataset.urgency = current.today && current.remaining <= 3600 ? 'last-hour' : 'standard';
        renderDeliveryHeadline(headline, current);
        copy.textContent = current.today ? text(current.remaining <= 3600 ? 'last_hour' : 'notice_clock') : text('notice_next');
        digits.hidden = !current.today;
      }
      if (current.today) digits.textContent = clock;
    });
  }
  function mountNotices() {
    if (!noticeState) return;
    const {destination, rate, rates, provinceName, generic} = noticeState;
    const key = JSON.stringify([destination.province, destination.zip, rates, generic]);
    const postalKey = JSON.stringify([destination.province, destination.zip]);
    document.querySelectorAll('[data-bmh-shipping-notice]').forEach(slot => {
      if (slot.dataset.destination === key && slot.children.length) {
        slot.hidden = false;
        return;
      }
      const content = noticeTemplate.content.cloneNode(true);
      const method = content.querySelector('[data-bmh-notice-method]');
      const route = content.querySelector('[data-bmh-notice-route]');
      const local = Boolean(rate.isFlex || rate.isLocal);
      slot.dataset.local = String(local);
      const price = amount(Math.round(Number(rate.price) * 100));
      const methodLabel = local ? text(rate.isFlex ? 'notice_flex' : 'notice_local') : rate.name;
      const free = Number(rate.price) === 0;
      method.classList.toggle('bmh-shipping-notice__free', free);
      method.textContent = free ? text('free_shipping') : methodLabel;
      route.textContent = free ? methodLabel : (generic ? text('from', {amount: price}) : price);
      const compact = Boolean(slot.closest('.cart-drawer__summary, .cart-page__summary'));
      slot.dataset.compact = String(compact);
      const postal = content.querySelector('[data-bmh-notice-postal]');
      postal.textContent = destination.zip ? text('postal_destination', {zip: destination.zip}) : '';
      postal.hidden = !destination.zip;
      const headline = content.querySelector('[data-bmh-notice-headline]');
      headline.hidden = !local;
      const urgency = content.querySelector('.bmh-shipping-notice__urgency');
      urgency.hidden = !local;
      const detail = content.querySelector('.bmh-shipping-notice__destination');
      detail.textContent = text('notice_destination', {destination: destination.zip ? text('postal_destination', {zip: destination.zip}) : provinceName});
      const options = content.querySelector('.bmh-shipping-notice__options');
      options.hidden = rates.length < 2;
      options.open = slot.dataset.postalKey === postalKey && Boolean(slot.querySelector('.bmh-shipping-notice__options')?.open);
      for (const option of rates) {
        const line = document.createElement('div');
        const label = document.createElement('span');
        const cost = document.createElement('strong');
        label.textContent = option.name;
        cost.textContent = Number(option.price) === 0 ? text('free') : amount(Math.round(Number(option.price) * 100));
        line.append(label, cost);
        options.append(line);
      }
      const estimate = content.querySelector('.bmh-shipping-notice__estimate');
      estimate.hidden = !generic;
      slot.replaceChildren(content);
      slot.dataset.destination = key;
      slot.dataset.postalKey = postalKey;
      delete slot.dataset.phase;
      slot.hidden = false;
    });
    tickNotices();
    // Cart sections can replace the notice without inserting its outer element.
    if (rate.isFlex || rate.isLocal) noticeTimer ||= setInterval(() => { if (!document.hidden) tickNotices(); }, 1000);
    else { clearInterval(noticeTimer); noticeTimer = null; }
  }
  function updateNotices(destination, rates, generic) {
    const zone = rules.zones.find(item => item.provinces.some(p => p.code === destination.province));
    const rate = rates.find(item => item.isFlex || item.isLocal) || [...rates].sort((a, b) => Number(a.price) - Number(b.price))[0];
    if (!zone || !rate) { clearNotices(); return; }
    const provinceName = zone.provinces.find(item => item.code === destination.province).name;
    noticeState = {destination, rate, rates, provinceName, generic};
    mountNotices();
  }
  async function refreshNotices(cart) {
    const requestId = ++noticeRequest;
    noticeController?.abort();
    if (!readDestination()) { clearNotices(); return; }
    let deadline;
    let destination;
    try {
      await loadRules();
      if (requestId !== noticeRequest) return;
      destination = readDestination();
      if (!destination) { clearNotices(); return; }
      const currentController = new AbortController();
      noticeController = currentController;
      deadline = setTimeout(() => currentController.abort(), 6000);
      const currentCart = cart || await readCart(currentController.signal);
      if (requestId !== noticeRequest) return;
      const zone = rules.zones.find(item => item.provinces.some(p => p.code === destination.province));
      const context = currentCart.item_count ? {total: currentCart.total_price, weight: currentCart.total_weight} : productContext();
      updateNotices(destination, preferredRates(includeLocalDelivery(configuredRates(zone, context), destination)), true);
    } catch {
      if (requestId !== noticeRequest) return;
      const zone = rules?.zones.find(item => item.provinces.some(p => p.code === destination?.province));
      if (zone) updateNotices(destination, preferredRates(includeLocalDelivery(configuredRates(zone, null), destination)), true);
      else clearNotices();
    } finally {
      clearTimeout(deadline);
    }
  }
  function addDeliveryWindow(row) {
    const settings = rules.deliverySchedule;
    if (!settings) return;
    const card = document.createElement('div');
    card.className = 'bmh-delivery-window';
    const headline = document.createElement('h3');
    headline.className = 'bmh-delivery-window__title';
    headline.setAttribute('aria-live', 'polite');
    const prompt = document.createElement('p');
    prompt.className = 'bmh-delivery-window__prompt';
    const clock = document.createElement('div');
    clock.className = 'bmh-delivery-clock';
    clock.setAttribute('role', 'timer');
    clock.setAttribute('aria-live', 'off');
    clock.setAttribute('aria-label', text('countdown_label'));
    const digits = ['hours', 'minutes', 'seconds'].map(unit => {
      const cell = document.createElement('span');
      cell.className = 'bmh-delivery-clock__cell';
      const value = document.createElement('span');
      value.className = 'bmh-delivery-clock__value';
      const label = document.createElement('span');
      label.className = 'bmh-delivery-clock__unit';
      label.textContent = text(unit);
      cell.append(value, label);
      clock.append(cell);
      return value;
    });
    const detail = document.createElement('p');
    detail.className = 'bmh-delivery-window__detail';
    const schedule = document.createElement('p');
    schedule.className = 'bmh-delivery-window__schedule';
    schedule.textContent = text('delivery_schedule');
    card.append(headline, prompt, clock, detail, schedule);
    row.append(card);
    const locale = document.documentElement.lang?.startsWith('es') ? 'es-AR' : 'en-US';
    const dayFormat = new Intl.DateTimeFormat(locale, {timeZone: 'UTC', weekday: 'long', day: 'numeric', month: 'long'});
    let phase;
    const update = () => {
      const current = deliveryWindow(new Date(), settings);
      const day = dayFormat.format(current.next);
      const lastHour = current.today && current.remaining <= 3600;
      const nextPhase = `${current.today}:${current.businessDay}:${day}:${lastHour}`;
      if (phase !== nextPhase) {
        phase = nextPhase;
        card.dataset.phase = current.today ? 'today' : 'next';
        card.dataset.urgency = lastHour ? 'last-hour' : 'standard';
        renderDeliveryHeadline(headline, current);
        prompt.textContent = current.today ? text(lastHour ? 'last_hour' : 'buy_within') : text(current.businessDay ? 'cutoff_passed' : 'closed_day');
        clock.hidden = !current.today;
        detail.hidden = !current.today;
        detail.textContent = text('today_after_cutoff', {day});
      }
      if (current.today) {
        const values = [Math.floor(current.remaining / 3600), Math.floor(current.remaining / 60) % 60, current.remaining % 60];
        values.forEach((value, index) => { digits[index].textContent = String(value).padStart(2, '0'); });
      }
    };
    update();
    clearInterval(deliveryTimer);
    deliveryTimer = setInterval(update, 1000);
  }
  function renderRates(rates, zone, context, generic) {
    results.replaceChildren();
    if (!rates.length) {
      status.textContent = text('no_rates');
      return;
    }
    for (const rate of rates.sort((a, b) => Number(Boolean(b.isLocal)) - Number(Boolean(a.isLocal)) || Number(a.price) - Number(b.price))) {
      const row = document.createElement('div');
      row.className = 'bmh-shipping-result';
      if (rate.isLocal || rate.isFlex) row.classList.add('bmh-shipping-result--priority');
      row.style.setProperty('--bmh-rate-index', results.children.length);
      if (Number(rate.price) === 0) row.classList.add('bmh-shipping-result--free');
      const name = document.createElement('span');
      name.className = 'bmh-shipping-result__name';
      name.textContent = rate.name;
      const price = document.createElement('span');
      price.className = 'bmh-shipping-result__price';
      const cents = Math.round(Number(rate.price) * 100);
      price.textContent = cents === 0 ? text('free') : (generic ? text('from', {amount: amount(cents)}) : amount(cents));
      row.append(name, price);
      const details = [];
      if (generic && cents === 0) {
        const maximum = rate.conditions?.find(condition => condition.field === 'TOTAL_WEIGHT' && condition.operator === 'LESS_THAN_OR_EQUAL_TO');
        if (maximum) details.push(text('up_to_weight', {weight: maximum.value / 1000}));
      }
      const minimum = freeMinimum(zone, rate.name, context?.weight || 0);
      if (cents > 0 && minimum !== null && (!context || context.total < minimum)) {
        details.push(text('free_from', {amount: amount(minimum)}));
        if (context) details.push(text('remaining', {amount: amount(minimum - context.total)}));
      }
      if (rate.description) details.push(rate.description);
      if (details.length) {
        const detail = document.createElement('span');
        detail.className = 'bmh-shipping-result__detail';
        detail.textContent = details.join(' · ');
        row.append(detail);
      }
      results.append(row);
      if (rate.isLocal || rate.isFlex) addDeliveryWindow(row);
    }
    footnote.hidden = false;
    dialog.dataset.state = 'ready';
    if (done) done.hidden = false;
  }
  async function calculate({autoClose = true} = {}) {
    reset();
    const sequence = quoteSequence;
    if (!province.value || !rules || !dialog.open || dialog.dataset.closing) return;
    showZip(Boolean(province.value));
    if (zip.required && !zip.checkValidity()) {
      status.textContent = text('enter_zip');
      return;
    }
    const destination = {province: province.value, zip: zip.value.trim().toUpperCase(), name: province.selectedOptions[0].textContent};
    if (/^[A-Z]/.test(destination.zip) && destination.zip[0] !== destination.province) {
      status.textContent = text('province_zip_mismatch');
      dialog.dataset.state = 'error';
      return;
    }
    const zone = zoneFor();
    if (!zone) return;
    const currentController = new AbortController();
    controller = currentController;
    const signal = currentController.signal;
    const current = () => controller === currentController && sequence === quoteSequence && dialog.open && !dialog.dataset.closing;
    const deadline = setTimeout(() => currentController.abort(), 6000);
    submit.disabled = true;
    submit.textContent = text('loading');
    status.textContent = text('loading');
    dialog.dataset.state = 'loading';
    submit.setAttribute('aria-busy', 'true');
    const present = (rates, context, generic, allowClose = autoClose) => {
      if (!current()) return;
      const selectedRates = preferredRates(includeLocalDelivery(rates, destination));
      renderRates(selectedRates, zone, context, generic);
      const saved = validDestination({...destination, savedAt: Date.now()});
      if (saved && selectedRates.length) {
        writeDestination(saved);
        // The quote already has its rates. Never block the card or modal on another cart request.
        ++noticeRequest;
        noticeController?.abort();
        clearTimeout(noticeRefreshTimer);
        updateNotices(saved, selectedRates, generic);
        if (allowClose) {
          autoCloseTimer = setTimeout(() => {
            if (sequence === quoteSequence && dialog.open) close();
          }, 500);
        }
      }
    };
    let cart;
    try {
      cart = await readCart(signal);
      if (!current() || signal.aborted) return;
      const context = cart.item_count ? {total: cart.total_price, weight: cart.total_weight} : productContext();
      let rates;
      let generic = !context;
      if (cart.item_count) {
        rates = await liveRates(destination, signal, cart);
        if (!current() || signal.aborted) return;
        status.textContent = text('cart_context', {amount: amount(cart.total_price)});
        if (!rates.length) {
          rates = configuredRates(zone, context);
          generic = true;
          status.textContent = text('fallback_context');
        }
      } else {
        rates = configuredRates(zone, context);
        status.textContent = context ? text('product_context', {quantity: context.quantity, title: context.title}) : text('generic_context');
      }
      present(rates, context, generic);
    } catch (error) {
      if (!current()) return;
      if (error.zipRequired) {
        forcedZip = true;
        showZip(true);
        status.textContent = text('need_zip');
        zip.focus({preventScroll: true});
      } else {
        // Known merchant rates remain an estimate; checkout confirms the final quote.
        status.textContent = text('fallback_context');
        const context = cart?.item_count ? {total: cart.total_price, weight: cart.total_weight} : productContext();
        present(configuredRates(zone, context), context, true);
      }
    } finally {
      clearTimeout(deadline);
      if (current()) {
        controller = null;
        submit.disabled = false;
        submit.removeAttribute('aria-busy');
        if (dialog.dataset.state === 'loading') dialog.dataset.state = 'idle';
        submit.textContent = text('calculate');
      }
    }
  }
  document.addEventListener('click', async event => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-bmh-shipping-open]');
    if (!trigger) return;
    event.preventDefault();
    clearTimeout(closingTimer);
    delete dialog.dataset.closing;
    reset();
    const sequence = ++openSequence;
    opener = trigger;
    openerControl = trigger.closest('.bmh-shipping-control');
    if (!dialog.open) dialog.showModal();
    dialog.scrollTop = 0;
    document.documentElement.classList.add('bmh-shipping-open');
    try {
      await loadRules();
      if (!dialog.open || sequence !== openSequence) return;
      const saved = readDestination();
      if (saved) {
        province.value = saved.province;
        forcedZip = Boolean(saved.zip) && saved.province !== 'B';
        showZip(true);
        zip.value = saved.zip;
      }
      reset();
      (zip.required ? zip : province).focus({preventScroll: true});
    } catch {
      if (dialog.open && sequence === openSequence) status.textContent = text('error');
    }
  });
  const release = () => {
    ++openSequence;
    clearTimeout(closingTimer);
    delete dialog.dataset.closing;
    reset();
    document.documentElement.classList.remove('bmh-shipping-open');
  };
  const finishClose = () => {
    clearTimeout(closingTimer);
    if (dialog.open) dialog.close();
    release();
  };
  const close = () => {
    if (!dialog.open || dialog.dataset.closing) return;
    ++openSequence;
    ++quoteSequence;
    clearTimeout(autoCloseTimer);
    clearTimeout(timer);
    controller?.abort();
    controller = null;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { finishClose(); return; }
    dialog.dataset.closing = 'true';
    // A bounded fallback also releases the native modal if animation events are interrupted.
    closingTimer = setTimeout(finishClose, 300);
  };
  dialog.addEventListener('animationend', event => {
    if (event.target === dialog && event.animationName === 'bmh-shipping-dialog-leave' && dialog.dataset.closing) finishClose();
  });
  dialog.querySelector('[data-bmh-shipping-close]').addEventListener('click', close);
  done?.addEventListener('click', close);
  dialog.addEventListener('cancel', event => { event.preventDefault(); close(); });
  forget?.addEventListener('click', () => {
    destinationMemory = null;
    ratesCache = null;
    try { localStorage.removeItem(destinationKey); } catch {}
    try { sessionStorage.removeItem(destinationKey); sessionStorage.removeItem('bmh-shipping-destination'); } catch {}
    noticeRequest++;
    noticeController?.abort();
    clearNotices();
    reset();
    province.value = '';
    zip.value = '';
    forcedZip = false;
    showZip(false);
    forget.hidden = true;
    province.focus();
  });
  dialog.addEventListener('click', event => {
    if (event.target !== dialog) return;
    const rect = dialog.getBoundingClientRect();
    if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) close();
  });
  dialog.addEventListener('close', () => {
    if (dialog.open) return;
    release();
    const target = opener?.isConnected && opener.getClientRects().length ? opener :
      [...(openerControl?.querySelectorAll('[data-bmh-shipping-open]') || [])].find(button => button.getClientRects().length);
    target?.focus({preventScroll: true});
  });
  province.addEventListener('change', () => {
    forcedZip = false;
    zip.value = '';
    reset();
    showZip(Boolean(province.value));
  });
  zip.addEventListener('input', () => {
    reset();
    clearTimeout(timer);

  });
  form.addEventListener('submit', async event => {
    event.preventDefault();
    clearTimeout(timer);
    const sequence = openSequence;
    try {
      await loadRules();
      if (dialog.open && sequence === openSequence && !dialog.dataset.closing && form.reportValidity()) calculate();
    } catch { status.textContent = text('error'); }
  });
  document.addEventListener('bmh:cart-changing', () => {
    cartCache = null;
    ratesCache = null;
    clearTimeout(timer);
    clearTimeout(noticeRefreshTimer);
    noticeRequest++;
    noticeController?.abort();
    reset();
  });
  document.addEventListener('bmh:cart-change-error', () => {
    refreshNotices();
    if (dialog.open && !dialog.dataset.closing && province.value && form.checkValidity()) calculate();
  });
  for (const eventName of ['cart:update', 'discount:update']) {
    document.addEventListener(eventName, event => {
      cartCache = null;
      ratesCache = null;
      const wasQuoting = dialog.dataset.state === 'loading' || dialog.dataset.state === 'ready';
      if (!dialog.dataset.closing) reset();
      if (wasQuoting && dialog.open && !dialog.dataset.closing && province.value && form.checkValidity()) calculate();
      clearTimeout(noticeRefreshTimer);
      const cart = event.detail?.resource;
      noticeRefreshTimer = setTimeout(() => refreshNotices(Array.isArray(cart?.items) ? cart : undefined), 200);
    });
  }
  window.addEventListener('storage', event => {
    if (event.key === destinationKey || event.key === null) {
      try { sessionStorage.removeItem(destinationKey); sessionStorage.removeItem('bmh-shipping-destination'); } catch {}
      destinationMemory = null;
      const saved = readDestination();
      if (dialog.open) { refreshNotices(); return; }
      reset();
      province.value = saved?.province || '';
      forcedZip = Boolean(saved?.zip) && saved?.province !== 'B';
      showZip(Boolean(saved?.province));
      zip.value = saved?.zip || '';
      if (forget) forget.hidden = !saved;
      refreshNotices();
    }
  });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) tickNotices();
  });
  new MutationObserver(records => {
    if (records.some(record =>
      (record.target instanceof Element && record.target.matches('[data-bmh-shipping-notice]') && !record.target.children.length) ||
      [...record.addedNodes].some(node => node instanceof Element &&
        (node.matches('[data-bmh-shipping-notice]') || node.querySelector('[data-bmh-shipping-notice]'))))) mountNotices();
  }).observe(document.body, {childList: true, subtree: true});
  refreshNotices();
})();
